'use client';

import {
    Children,
    createContext,
    isValidElement,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ComponentPropsWithRef,
    type ReactElement,
    type ReactNode,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';
import { Button } from '@/shared-ui/atoms/button';
import { Slug as SlugAtom, type SlugProps as SlugAtomProps } from '@/shared-ui/atoms/slug';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';

import {
    getContentCardCtaLabel,
    resolveContentCardBackground,
    resolveContentCardCtaOverlayFromElement,
    resolveContentCardCtaTargetRel,
    resolveContentCardSpacing,
    type ContentCardButtonVariant,
    type ContentCardCtaOverlayState,
    type ContentCardTextAlign,
    type ContentCardTypographyWeight,
} from './content-card.shared';

type ContentCardContextValue = {
    setCta: (cta: ContentCardCtaOverlayState | null) => void;
    textAlign: ContentCardTextAlign;
    rounded: boolean;
};

const ContentCardContext = createContext<ContentCardContextValue | null>(null);

type ContentCardCtaContextValue = {
    variant: ContentCardButtonVariant;
    fullWidth: boolean;
};

const ContentCardCtaContext = createContext<ContentCardCtaContextValue | null>(null);

const useContentCardContext = (component: string) => {
    const context = useContext(ContentCardContext);
    if (!context) {
        throw new Error(`\`${component}\` must be used within \`ContentCard.Root\``);
    }
    return context;
};

const textAlignLayoutClasses: Record<ContentCardTextAlign, string> = {
    left: 'items-start text-left',
    center: 'items-center text-center',
};

const textAlignSlugClasses: Record<ContentCardTextAlign, string> = {
    left: 'self-start',
    center: 'self-center',
};

/** Typography `align` from card `textAlign` (avoids default `text-left` overriding inherited alignment). */
const contentCardTypographyAlign: Record<ContentCardTextAlign, NonNullable<TypographyProps['align']>> = {
    left: 'left',
    center: 'center',
};

const DEFAULT_CONTENT_GAP = 16;

function isImageChild(child: ReactNode): child is ReactElement {
    return isValidElement(child) && (child.type as { displayName?: string }).displayName === 'ContentCard.Image';
}

export interface RootProps extends React.ComponentPropsWithRef<'div'> {
    width?: number | string;
    textAlign?: ContentCardTextAlign;
    backgroundColor?: string;
    /** When false, the card image renders with square corners. Default `true`. */
    rounded?: boolean;
    contentGap?: number | string;
    contentPadding?: number | string;
    /** Padding above `content-card-body` (below the image), in px. `0` = none. */
    contentBodyPaddingTop?: number | string;
}

const Root = ({
    width,
    textAlign = 'left',
    backgroundColor,
    rounded = true,
    contentGap = DEFAULT_CONTENT_GAP,
    contentPadding = 0,
    contentBodyPaddingTop = 0,
    style,
    className,
    children,
    ref,
    ...props
}: RootProps) => {
    const [cta, setCta] = useState<ContentCardCtaOverlayState | null>(null);
    const resolvedBackground = resolveContentCardBackground(backgroundColor);
    const resolvedContentGap = resolveContentCardSpacing(contentGap) ?? `${DEFAULT_CONTENT_GAP}px`;
    const resolvedContentPadding = resolveContentCardSpacing(contentPadding);
    const resolvedContentBodyPaddingTop =
        contentBodyPaddingTop === 0 || contentBodyPaddingTop === '0'
            ? undefined
            : resolveContentCardSpacing(contentBodyPaddingTop);

    const { leadingImage, bodyChildren } = useMemo(() => {
        const childArray = Children.toArray(children);
        if (childArray.length > 0 && isImageChild(childArray[0])) {
            return { leadingImage: childArray[0], bodyChildren: childArray.slice(1) };
        }
        return { leadingImage: null, bodyChildren: childArray };
    }, [children]);

    const contextValue = useMemo(
        () => ({
            setCta,
            textAlign,
            rounded,
        }),
        [textAlign, rounded]
    );

    return (
        <ContentCardContext.Provider value={contextValue}>
            <div
                data-slot="content-card"
                data-text-align={textAlign}
                ref={ref}
                className={cn('relative w-full bg-transparent', className)}
                style={{
                    ...style,
                    ...(width !== undefined ? { width } : {}),
                    ...(resolvedBackground ? { backgroundColor: resolvedBackground } : {}),
                }}
                {...props}>
                {cta ? (
                    <a
                        data-slot="content-card-overlay-link"
                        href={cta.href}
                        target={cta.targetWindow}
                        rel={cta.rel}
                        aria-label={cta.label}
                        className="absolute inset-0 z-10">
                        <span className="sr-only">{cta.label}</span>
                    </a>
                ) : null}
                {leadingImage}
                {bodyChildren.length > 0 ? (
                    <div
                        data-slot="content-card-body"
                        data-content-body-padding-top={resolvedContentBodyPaddingTop}
                        className={cn(
                            'pointer-events-none relative z-20 flex w-full flex-col',
                            textAlignLayoutClasses[textAlign]
                        )}
                        style={{
                            gap: resolvedContentGap,
                            ...(resolvedContentPadding ? { padding: resolvedContentPadding } : {}),
                            ...(resolvedContentBodyPaddingTop ? { paddingTop: resolvedContentBodyPaddingTop } : {}),
                        }}>
                        {bodyChildren}
                    </div>
                ) : null}
            </div>
        </ContentCardContext.Provider>
    );
};
Root.displayName = 'ContentCard.Root';

export interface ImageProps extends React.ComponentPropsWithRef<'img'> {
    /** When false, image corners are square. Inherits from `ContentCard.Root` when omitted. */
    rounded?: boolean;
}

const Image = ({ className, alt = '', src, rounded: roundedProp, ref, ...props }: ImageProps) => {
    const cardContext = useContext(ContentCardContext);
    const rounded = roundedProp ?? cardContext?.rounded ?? true;

    if (!src) {
        return null;
    }
    return (
        <img
            data-slot="content-card-image"
            data-rounded={rounded ? 'true' : 'false'}
            ref={ref}
            src={src}
            alt={alt}
            className={cn('h-auto w-full bg-muted', rounded && 'rounded-sm', className)}
            loading="lazy"
            decoding="async"
            {...props}
        />
    );
};
Image.displayName = 'ContentCard.Image';

export interface IconProps extends React.ComponentPropsWithRef<'img'> {
    /** Sets the rendered height of the icon / SVG (width scales automatically). */
    height?: number | string;
    /** Additional margin below the icon (px number or CSS length). */
    marginBottom?: number | string;
}

const Icon = ({ className, alt = '', src, height, marginBottom, style, ref, ...props }: IconProps) => {
    if (!src) {
        return null;
    }

    const heightStyle =
        height !== undefined ? { height: typeof height === 'number' ? `${height}px` : height } : undefined;
    const resolvedMarginBottom = resolveContentCardSpacing(marginBottom);

    return (
        <span
            data-slot="content-card-icon"
            className="inline-flex w-fit items-center"
            style={resolvedMarginBottom ? { marginBottom: resolvedMarginBottom } : undefined}>
            <img
                ref={ref}
                src={src}
                alt={alt}
                className={cn('h-auto w-auto max-w-full object-contain', className)}
                style={{ ...style, ...heightStyle, ...(heightStyle ? { width: 'auto' as const } : {}) }}
                loading="lazy"
                decoding="async"
                {...props}
            />
        </span>
    );
};
Icon.displayName = 'ContentCard.Icon';

type ContentCardSlugProps = {
    children: ReactNode;
    className?: string;
    type?: SlugAtomProps['type'];
};

const Slug = ({ children, className, type = '1' }: ContentCardSlugProps) => {
    const { textAlign } = useContentCardContext('ContentCard.Slug');

    if (children == null) {
        return null;
    }

    return (
        <SlugAtom
            data-slot="content-card-slug"
            className={cn(textAlignSlugClasses[textAlign], className)}
            content="copy"
            type={type}>
            {children}
        </SlugAtom>
    );
};
Slug.displayName = 'ContentCard.Slug';

export type ContentCardTypographySectionProps = {
    children: ReactNode;
    className?: string;
    variant?: TypographyProps['variant'];
    weight?: ContentCardTypographyWeight;
};

const Subtitle = ({ children, className, variant = 'h5', weight }: ContentCardTypographySectionProps) => {
    const { textAlign } = useContentCardContext('ContentCard.Subtitle');

    if (children == null) {
        return null;
    }
    return (
        <Typography
            data-slot="content-card-subtitle"
            align={contentCardTypographyAlign[textAlign]}
            className={cn('leading-none', className)}
            weight={weight}
            variant={variant}>
            {children}
        </Typography>
    );
};
Subtitle.displayName = 'ContentCard.Subtitle';

const Title = ({ children, className, variant = 'amiri-h1', weight }: ContentCardTypographySectionProps) => {
    const { textAlign } = useContentCardContext('ContentCard.Title');

    if (children == null) {
        return null;
    }
    return (
        <Typography
            data-slot="content-card-heading"
            align={contentCardTypographyAlign[textAlign]}
            className={className}
            weight={weight}
            variant={variant}>
            {children}
        </Typography>
    );
};
Title.displayName = 'ContentCard.Title';

const Subheading = ({
    children,
    className,
    variant = 'amiri-subheading',
    weight,
}: ContentCardTypographySectionProps) => {
    const { textAlign } = useContentCardContext('ContentCard.Subheading');

    if (children == null) {
        return null;
    }
    return (
        <Typography
            data-slot="content-card-subheading"
            align={contentCardTypographyAlign[textAlign]}
            className={className}
            weight={weight}
            variant={variant}>
            {children}
        </Typography>
    );
};
Subheading.displayName = 'ContentCard.Subheading';

const Description = ({ children, className, variant = 'p', weight = 'light' }: ContentCardTypographySectionProps) => {
    const { textAlign } = useContentCardContext('ContentCard.Description');

    if (children == null) {
        return null;
    }
    return (
        <Typography
            data-slot="content-card-copy"
            align={contentCardTypographyAlign[textAlign]}
            className={className}
            variant={variant}
            weight={weight}>
            {children}
        </Typography>
    );
};
Description.displayName = 'ContentCard.Description';

type CtaLinkProps = ComponentPropsWithRef<'a'> & {
    asChild?: boolean;
    /** Visible label when `children` is omitted (anchor fallback only). */
    label?: string;
    variant?: ContentCardButtonVariant;
    fullWidth?: boolean;
    targetWindow?: '_self' | '_blank' | '_parent' | '_top';
    rel?: string;
};

const CtaLink = ({
    asChild = false,
    label,
    variant: variantProp,
    fullWidth: fullWidthProp,
    targetWindow,
    rel,
    target,
    href: hrefProp,
    className,
    children,
    ref,
    'aria-label': ariaLabel,
    ...props
}: CtaLinkProps) => {
    const { targetWindow: resolvedTarget, rel: resolvedRel } = resolveContentCardCtaTargetRel({
        targetWindow,
        target,
        rel,
    });
    const ctaFromParent = useContext(ContentCardCtaContext);
    const variant = variantProp ?? ctaFromParent?.variant ?? 'tertiary';
    const fullWidth = fullWidthProp ?? ctaFromParent?.fullWidth ?? false;
    const { setCta } = useContentCardContext('ContentCard.CtaLink');
    const linkChildren = children ?? label;
    const hasLink = asChild ? Boolean(children) : Boolean(linkChildren && hrefProp);

    const overlayState = useMemo((): ContentCardCtaOverlayState | null => {
        if (!hasLink) {
            return null;
        }
        if (asChild && isValidElement(children)) {
            return resolveContentCardCtaOverlayFromElement(children);
        }
        const overlayLabel = ariaLabel ?? label ?? getContentCardCtaLabel(linkChildren);
        if (!hrefProp || !overlayLabel) {
            return null;
        }
        return { href: hrefProp, label: overlayLabel, targetWindow: resolvedTarget, rel: resolvedRel };
    }, [ariaLabel, asChild, children, hasLink, hrefProp, label, linkChildren, resolvedRel, resolvedTarget]);

    useEffect(() => {
        setCta(overlayState);
        return () => {
            setCta(null);
        };
    }, [overlayState, setCta]);

    if (!hasLink || !overlayState) {
        return null;
    }

    const Comp = asChild ? Slot : 'a';

    return (
        <Button
            asChild
            variant={variant}
            className={cn(variant === 'tertiary' ? 'px-0 py-0' : undefined, fullWidth && 'flex w-full max-w-full')}>
            <Comp
                ref={ref as never}
                data-slot="content-card-cta-link"
                href={!asChild ? hrefProp : undefined}
                target={!asChild ? resolvedTarget : undefined}
                rel={!asChild ? resolvedRel : undefined}
                tabIndex={-1}
                aria-hidden="true"
                className={cn('no-underline', className, fullWidth && 'flex w-full justify-center')}
                {...props}>
                {linkChildren}
            </Comp>
        </Button>
    );
};
CtaLink.displayName = 'ContentCard.CtaLink';

type CtaProps = ComponentPropsWithRef<'div'> & {
    variant?: ContentCardButtonVariant;
    fullWidth?: boolean;
    /** Additional margin above the CTA (px number or CSS length). */
    marginTop?: number | string;
    children: ReactNode;
};

const Cta = ({
    variant: buttonVariant = 'tertiary',
    fullWidth = false,
    marginTop,
    className,
    style,
    children,
    ref,
    ...divProps
}: CtaProps) => {
    useContentCardContext('ContentCard.Cta');
    const resolvedMarginTop = resolveContentCardSpacing(marginTop);
    const ctaContextValue = useMemo(() => ({ variant: buttonVariant, fullWidth }), [buttonVariant, fullWidth]);

    return (
        <ContentCardCtaContext.Provider value={ctaContextValue}>
            <div
                ref={ref}
                data-slot="content-card-button"
                data-full-width={fullWidth ? 'true' : undefined}
                className={cn(fullWidth && 'flex w-full max-w-full self-stretch', className)}
                style={{
                    ...(resolvedMarginTop ? { marginTop: resolvedMarginTop } : {}),
                    ...style,
                }}
                {...divProps}>
                {children}
            </div>
        </ContentCardCtaContext.Provider>
    );
};
Cta.displayName = 'ContentCard.Button';

const ContentCard = {
    Root,
    Image,
    Slug,
    Icon,
    Subtitle,
    Title,
    Subheading,
    Description,
    Cta,
    CtaLink,
} as const;

export { ContentCard };
